import { useMemo, useState } from 'react';

import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Dialog,
  IconButton,
  Typography,
  Link,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Column } from 'react-table';

import { fetchTranches, signTranche } from 'api';
import { Table } from 'components/Table';
import { convertToDateShort, getErrorDetail, getPageCount } from 'helpers';
import { useUrlPagination, useControlledPagination } from 'hooks';
import { useSnackContext } from 'hooks/useSnackContext';
import { DerogCell } from 'pages/tranche/DerogCell';
import { SignedTrancheButtons } from 'pages/tranche/SignedTrancheButtons';
import { TrancheProps } from 'types';

import {
  DashboardWrapper,
  CellWrapper,
  NullValueMarker,
} from '../styledComponents';

export const AuditorTrancheTable = () => {
  const { setSnack } = useSnackContext();

  const { controlledRows, controlledPageIndex } = useUrlPagination();
  const config = useMemo(
    () => ({
      params: {
        page_number: controlledPageIndex,
        limit: controlledRows,
        tranche_status: ['sent_to_state'],
      },
    }),
    [controlledPageIndex, controlledRows],
  );
  const { dataArray, dataLoaded, count, updateData } = useControlledPagination(
    fetchTranches,
    config,
  );

  const [sendToStateAlert, setSendToStateAlert] = useState({
    open: false,
    trancheId: null,
    sequence_no: null,
  });

  const columns = useMemo(
    (): Array<Column<TrancheProps>> => [
      {
        id: 'inspect',
        Header: () => {
          return (
            <CellWrapper aria-hidden>
              <p aria-label='Inspect tranche' />
            </CellWrapper>
          );
        },
        Cell: (row: any) => {
          const tranche = row.row.original as TrancheProps;

          return (
            <CellWrapper>
              <Link
                variant='body1'
                aria-label={'Inspect Tranche number ' + row.row.original.id}
                component={RouterLink}
                to={{
                  pathname: 'reviewed_candidates',
                  search: `tranche_id=${tranche.id}&include_siv_ineligible=1`,
                }}
              >
                INSPECT
              </Link>
            </CellWrapper>
          );
        },
      },
      // {
      //   id: 'tranche_number',
      //   Header: 'Tranche #',
      //   accessor: 'id',
      //   disableFilters: true,
      //   disableSortBy: false,
      // },
      {
        id: 'tranche_sequence',
        Header: 'Tranche #',
        accessor: 'sequence_no',
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'sent',
        Header: 'Sent To State',
        Cell: (row: any) => {
          const { signature_time } = row.row.original as TrancheProps;

          return signature_time ? (
            <Typography variant='body1'>
              {convertToDateShort(signature_time)}
            </Typography>
          ) : (
            <NullValueMarker />
          );
        },
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'candidate_count',
        Header: '# of Entries',
        accessor: 'candidate_count',
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'clear',
        Header: 'Clear | Derog',
        Cell: (row: any) => {
          const tranche = row.row.original as TrancheProps;

          return <DerogCell {...tranche} />;
        },
        width: 100,
        disableFilters: true,
        disableSortBy: false,
      },
      {
        id: 'action',
        Header: '',
        accessor: 'status',
        Cell: (row: any) => {
          const { status, id } = row.row.original;

          switch (status) {
            case 'sent_to_state':
              return <SignedTrancheButtons trancheId={id} />;
            default:
              return '';
          }
        },
        disableFilters: true,
        disableSortBy: false,
      },
    ],
    [],
  );
  return (
    <>
      <DashboardWrapper>
        <Table<TrancheProps>
          count={count}
          loading={!dataLoaded}
          useTableOptions={{
            data: dataArray,
            columns: columns,
            manualPagination: true,
            pageCount: getPageCount(count, controlledRows),
            autoResetPage: false,

            // defaultCanSort: false,
            // defaultCanFilter: false,
            // canFilter: false,
            // canSort:false,
            disableSort: true,
          }}
        />
      </DashboardWrapper>
      <Dialog open={sendToStateAlert.open}>
        <IconButton
          aria-label='cancel'
          sx={{ marginLeft: 'auto' }}
          onClick={() =>
            setSendToStateAlert({
              open: false,
              trancheId: null,
              sequence_no: null,
            })
          }
        >
          <Close fontSize='small' />
        </IconButton>
        <Box sx={{ padding: 3 }}>
          <Typography
            sx={{ mb: 1 }}
            variant='h5'
          >{`You are sending Tranche ${sendToStateAlert.sequence_no} to the State Department`}</Typography>
          <Typography sx={{ mb: 2 }} variant='body1'>
            Clicking 'Send' below will append your signature to this Tranche and
            submit to State.
          </Typography>
          <Box sx={{ textAlign: 'center' }}>
            <Button
              variant='contained'
              sx={{ mr: 1 }}
              disabled={!sendToStateAlert.sequence_no}
              onClick={async () => {
                if (sendToStateAlert.trancheId) {
                  try {
                    const { data } = await signTranche(
                      sendToStateAlert.trancheId,
                    );
                    updateData(data);
                  } catch (e) {
                    console.log(e);
                    setSnack((props) => ({
                      ...props,
                      severity: 'error',
                      open: true,
                      message: getErrorDetail({
                        error: e,
                        fallback: 'Failed To Send To State',
                      }),
                      error: e as Error,
                    }));
                  }
                  setSendToStateAlert({
                    open: false,
                    trancheId: null,
                    sequence_no: null,
                  });
                }
              }}
            >
              Send To State
            </Button>
            <Button
              sx={{ ml: 1 }}
              variant='outlined'
              onClick={() =>
                setSendToStateAlert({
                  open: false,
                  trancheId: null,
                  sequence_no: null,
                })
              }
            >
              CANCEL
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};
